import { FunctionComponent } from 'react';
import clsx from 'clsx';
import styles from './DaycareDataInfo.module.scss';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { DataVaraible, AppointmentMultiple, PetServiceWrapper, itemTypes, ItemData } from 'model/NewMultipleBooking';
import { Product } from '@spike/product-model';
import AddOnService from '@spike/addon-service-model';
import Service, { DaycareService } from 'model/Service';
import { PetService } from '@spike/new-booking-model';
import { Moment } from 'moment-timezone';
import ServiceOptions from './ServiceOptions';
import FeedingsAndMedicationsInfo from './FeedingsAndMedicationsInfo';
import { resolveAppointmentCardLineBase, resolveAppointmentCardLineDisplay } from '../utils/membershipDisplayPrice';

interface Props {
    hasFeedings: boolean;
    hasMedications: boolean;
    data: DataVaraible;
    index: number;
    appts: Array<DataVaraible>;
    appointment: AppointmentMultiple;
    onEdit: (
        petId: number,
        dateEdit: Moment,
        staffId?: number,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes,
        dateEnd?: Moment
    ) => void;
    onDeleteAppointment: (petId: number, value?: number | string, type?: itemTypes, dateToRemove?: Moment) => void;
    onShowActions: (
        petId: number,
        dateEdit: Moment,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        staffId?: number,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes,
        dateEnd?: Moment
    ) => void;
}

export const DaycareDataInfo: FunctionComponent<Props> = props => {
    const services = useSelector<RootState, Array<Service>>(state => state.services.services);
    const daycareServices = useSelector<RootState, Array<DaycareService>>(
        state => state.services.daycareServices || []
    );
    const petServices = useSelector<RootState, Array<PetService>>(state => state.newBookings.petServices);

    const getVariableDurationParsed = (): { duration_option?: string; durationOption?: string } | undefined => {
        if (!props.data.service?.variableDuration) return undefined;
        try {
            return JSON.parse(props.data.service.variableDuration);
        } catch {
            return undefined;
        }
    };

    const getDuration = () => {
        const parsed = getVariableDurationParsed();
        if (parsed) {
            const opt = parsed?.duration_option ?? parsed?.durationOption;
            if (opt === 'full_day') {
                return 'Full Day';
            } else if (opt === 'half_day') {
                return 'Half Day';
            } else if (opt === 'hourly') {
                return 'Hourly';
            } else {
                return '';
            }
        }

        return '';
    };

    const getRange = () => {
        const duration = props.data.service?.duration;
        if (duration) {
            const hours = duration?.hours || 0;
            const minutes = duration?.minutes || 0;
            const totalHours = hours + minutes / 60;
            if (totalHours <= 2) {
                return 'Up to 2 hours';
            } else if (totalHours <= 4) {
                return 'Up to 4 hours';
            } else if (totalHours <= 6) {
                return 'Up to 6 hours';
            } else if (totalHours <= 8) {
                return 'Up to 8 hours';
            } else if (totalHours <= 10) {
                return 'Up to 10 hours';
            } else if (totalHours <= 12) {
                return 'Up to 12 hours';
            } else {
                return 'More than 12 hours';
            }
        }

        return '';
    };

    const getDiscount = (data: DataVaraible) => {
        if (data.itemData?.discount != null) {
            return data.itemData.discount;
        }
        if (data.service?.enrollment?.id != null && data.service?.discount != null) {
            return data.service.discount;
        }
        return undefined;
    };

    const variableDurationParsed = getVariableDurationParsed();
    const isHourlyDaycare =
        (variableDurationParsed?.duration_option ?? variableDurationParsed?.durationOption) === 'hourly';

    const hourlyDurationMinutes =
        isHourlyDaycare && props.data.date
            ? props.data.dateEnd != null
                ? Math.max(0, props.data.dateEnd.diff(props.data.date, 'minutes'))
                : props.data.service?.durationInMinutes || 0
            : 0;
    const hourlyBillableHours = isHourlyDaycare ? Math.max(0, Math.ceil(hourlyDurationMinutes / 60)) : 0;

    const serviceId = props.data.service?.service?.id;
    const catalogFallback =
        daycareServices.find(s => s.serviceId === serviceId)?.price ??
        petServices.find(ps => ps.service.id === serviceId)?.price;

    const slotLinePrice = Number(props.data.service?.price) || 0;
    const lineBase = resolveAppointmentCardLineBase({
        slotPrice: slotLinePrice,
        itemDataPrice: props.data.itemData?.price ?? catalogFallback,
        beforePrice: props.data.service?.beforePrice,
        customPriceAmount: props.data.service?.customPriceAmount,
        hourlyMultiplier: isHourlyDaycare ? hourlyBillableHours : undefined
    });
    const {
        displayPrice: resolvedDisplayPrice,
        originalPrice: displayPriceBeforeDiscount,
        hasDisplayDiscount
    } = resolveAppointmentCardLineDisplay({
        enrollmentId: props.data.service?.enrollment?.id,
        membershipItem: props.data.service?.membershipItem,
        slotPrice: slotLinePrice,
        basePrice: lineBase,
        itemDiscount: getDiscount(props.data)
    });

    return (
        <>
            {props.index === 0 && (
                <div className={styles.dateContainer}>
                    <span className={styles.date}>{props.data.date?.format('dddd, DD MMM YYYY')}</span>
                </div>
            )}
            <div
                className={styles.serviceStaffContainer}
                key={props.data.service?.service?.id}
            >
                <div className={styles.itemContainer}>
                    <div
                        className={styles.emptyBox}
                        style={{
                            borderLeftColor:
                                services.find((serv: Service) => serv.id === props.data.service?.service?.id)?.color ??
                                '#ffffff'
                        }}
                    />
                    <div
                        className={clsx(styles.divide, {
                            [styles.withoutDivider]: props.index === props.appts.length - 1
                        })}
                        style={{ width: '91%' }}
                    >
                        <span className={styles.serviceName}>
                            <span className={clsx(styles.serviceNameText, styles.rightSpace)}>
                                {props.data.service?.service?.name}
                            </span>
                            {`${props.data.itemData?.quantity ? '(' : ' · '}${
                                props.data.itemData?.quantity ? props.data.itemData?.quantity : ''
                            }${props.data.itemData?.quantity ? ') ·' : ''}`}
                            {props.data.service?.service && (
                                <>
                                    <div className={styles.serviceData}>
                                        {hasDisplayDiscount && (
                                            <span className={clsx(styles.beforePrice, styles.price)}>
                                                ${displayPriceBeforeDiscount.toFixed(2)}
                                            </span>
                                        )}
                                        {hasDisplayDiscount && (
                                            <span className={styles.price}>${resolvedDisplayPrice.toFixed(2)}</span>
                                        )}
                                    </div>
                                    {!hasDisplayDiscount && (
                                        <span className={styles.price}>{'$' + resolvedDisplayPrice.toFixed(2)}</span>
                                    )}
                                </>
                            )}
                        </span>
                        <div className={styles.serviceDataContainer}>
                            <div className={styles.dataContainer}>
                                {props.data.service?.service && (
                                    <span className={styles.priceAndDuration}>
                                        {props.data.date?.format('LT')}
                                        <span className={styles.dot}>·</span>
                                    </span>
                                )}
                                <div className={styles.dataContainer}>
                                    <span className={clsx(styles.priceAndDuration, styles.spacingDuration)}>
                                        {getDuration()}
                                    </span>
                                </div>
                                <span className={styles.priceAndDuration}>
                                    <span className={styles.dot}>·</span>
                                    {getRange()}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        className={clsx(styles.divide, styles.serviceOptionsContainer, {
                            [styles.withoutDivider]: props.index === props.appts.length - 1
                        })}
                    >
                        <ServiceOptions
                            className={styles.serviceOptions}
                            item="Service"
                            onClickMobile={() =>
                                props.onShowActions(
                                    props.appointment.petId,
                                    props.data.date,
                                    props.data.itemData,
                                    props.data.service,
                                    props.data.staffId,
                                    props.data.product,
                                    props.data.addOn,
                                    props.data.customDescription,
                                    itemTypes.service,
                                    props.data.dateEnd
                                )
                            }
                            onEdit={() =>
                                props.onEdit(
                                    props.appointment.petId,
                                    props.data.date,
                                    props.data.staffId,
                                    props.data.itemData,
                                    props.data.service,
                                    props.data.product,
                                    props.data.addOn,
                                    props.data.customDescription,
                                    itemTypes.service,
                                    props.data.dateEnd
                                )
                            }
                            onDeleteAppointment={() =>
                                props.onDeleteAppointment(
                                    props.appointment.petId,
                                    props.data.service?.service?.id,
                                    itemTypes.service,
                                    props.data.date
                                )
                            }
                        />
                    </div>
                </div>
                <FeedingsAndMedicationsInfo
                    hasFeedings={props.hasFeedings}
                    hasMedications={props.hasMedications}
                />
            </div>
        </>
    );
};

export default DaycareDataInfo;
